<template>
  <!-- Begin page -->
  <div id="layout-wrapper">
    <navigation-bar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Daily Attendance Report</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                    <li class="breadcrumb-item">Reports</li>
                    <li class="breadcrumb-item active">Daily Attendance</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <!-- end page title -->

          <!-- System configuration -->
          <div class="card mb-4" v-if="isAdmin">
            <div class="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 class="mb-0">System Configuration</h5>
              <button class="btn btn-sm btn-primary" @click="saveConfiguration" :disabled="!configurationChanged">
                <i class="mdi mdi-content-save me-1"></i>
                <span v-if="configSaving">Saving...</span>
                <span v-else>Save Changes</span>
              </button>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-check form-switch mb-3">
                    <input class="form-check-input" type="checkbox" id="enableRollCalls"
                           v-model="configuration.enableRollCalls" @change="onConfigurationChange">
                    <label class="form-check-label" for="enableRollCalls">Enable Roll Calls</label>
                  </div>
                  <p class="text-muted small" v-if="configuration.enableRollCalls">
                    Roll calls are enabled. Employees will be marked as absent if they miss required roll calls.
                  </p>
                  <p class="text-muted small" v-else>
                    Roll calls are disabled. Absence will be determined by check-in/check-out records only.
                  </p>
                </div>
                <div class="col-md-6" v-if="configuration.enableRollCalls">
                  <div class="form-group">
                    <label class="form-label">Minimum Roll Calls Required</label>
                    <select class="form-select" v-model="configuration.minRequiredRollCalls" @change="onConfigurationChange">
                      <option value="1">At least 1 roll call</option>
                      <option value="2">At least 2 roll calls</option>
                      <option value="3">All 3 roll calls</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label">Minimum Working Hours</label>
                    <div class="input-group">
                      <input type="number" class="form-control" v-model="configuration.minWorkingHours"
                             min="0" max="24" step="0.5" @change="onConfigurationChange">
                      <span class="input-group-text">hours</span>
                    </div>
                    <small class="text-muted">Employees with fewer hours will be highlighted</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check form-switch mb-3">
                    <input class="form-check-input" type="checkbox" id="strictMode"
                           v-model="configuration.strictMode" @change="onConfigurationChange">
                    <label class="form-check-label" for="strictMode">Strict Attendance Mode</label>
                  </div>
                  <p class="text-muted small">
                    When enabled, employees must have both check-in and check-out records to be considered present
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Selection and Export Controls -->
          <div class="card mb-4">
            <div class="card-body">
              <div class="row align-items-center">
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label">Report Date</label>
                    <input type="date" class="form-control" v-model="selectedDate"
                           @change="loadAttendanceData" :max="maxDate">
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label">Department Filter</label>
                    <select class="form-select" v-model="selectedDepartment" @change="loadAttendanceData">
                      <option value="">All Departments</option>
                      <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                        {{ dept.departmentName }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="d-flex gap-2 align-items-end" style="height: 100%;">
                    <button class="btn btn-primary" @click="loadAttendanceData" :disabled="isLoading">
                      <i class="mdi mdi-refresh me-1"></i>
                      <span v-if="isLoading">Loading...</span>
                      <span v-else>Refresh</span>
                    </button>
                    <button class="btn btn-success" @click="exportToExcel" :disabled="isExporting || !reportData.length">
                      <i class="mdi mdi-file-excel me-1"></i>
                      <span v-if="isExporting">Exporting...</span>
                      <span v-else>Export Excel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Employees</p>
                      <h5 class="mb-0">{{ dashboardStats.totalEmployees }}</h5>
                      <small class="text-muted">
                        <span class="text-success">{{ dashboardStats.presentEmployees }}</span> Present /
                        <span class="text-danger">{{ dashboardStats.absentEmployees }}</span> Absent
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title rounded-circle bg-primary text-white font-size-20">
                        <i class="mdi mdi-account-group"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Check-ins</p>
                      <h5 class="mb-0">{{ dashboardStats.totalCheckins }}</h5>
                      <small class="text-muted">
                        <span class="text-primary">{{ dashboardStats.remoteCheckins }}</span> Remote /
                        <span class="text-success">{{ dashboardStats.onsiteCheckins }}</span> Onsite
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title rounded-circle bg-success text-white font-size-20">
                        <i class="mdi mdi-calendar-check"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6" v-if="configuration.enableRollCalls">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Roll Call Response</p>
                      <h5 class="mb-0">{{ dashboardStats.rollCallResponses }}</h5>
                      <small class="text-muted">
                        {{ rollCallResponseRate }}% response rate
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title rounded-circle bg-warning text-white font-size-20">
                        <i class="mdi mdi-check-all"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Hours Worked</p>
                      <h5 class="mb-0">{{ dashboardStats.totalHours }} hrs</h5>
                      <small class="text-muted">
                        Avg {{ dashboardStats.averageHours }} hrs/employee
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title rounded-circle bg-info text-white font-size-20">
                        <i class="mdi mdi-clock-outline"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Search and Filters -->
          <div class="card mb-4">
            <div class="card-header bg-white border-bottom p-3">
              <div class="row align-items-center">
                <div class="col-md-6">
                  <div class="search-box">
                    <div class="position-relative">
                      <input type="text" class="form-control bg-light border-light rounded"
                             placeholder="Search by name or employee ID..."
                             v-model="searchQuery" @input="handleSearch">
                      <i class="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="d-flex justify-content-end gap-2">
                    <div class="btn-group" role="group">
                      <button type="button" class="btn btn-outline-primary"
                              :class="{ active: attendanceFilter === 'all' }"
                              @click="setAttendanceFilter('all')">
                        All Employees
                      </button>
                      <button type="button" class="btn btn-outline-success"
                              :class="{ active: attendanceFilter === 'present' }"
                              @click="setAttendanceFilter('present')">
                        Present Only
                      </button>
                      <button type="button" class="btn btn-outline-danger"
                              :class="{ active: attendanceFilter === 'absent' }"
                              @click="setAttendanceFilter('absent')">
                        Absent Only
                      </button>
                      <button type="button" class="btn btn-outline-warning"
                              :class="{ active: attendanceFilter === 'late' }"
                              @click="setAttendanceFilter('late')"
                              v-if="configuration.enableRollCalls">
                        Missed Roll Calls
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Attendance Table -->
          <div class="card">
            <div class="card-body">
              <div class="table-responsive position-relative">
                <!-- Loading overlay -->
                <div v-if="isLoading" class="position-absolute d-flex justify-content-center align-items-center w-100 h-100"
                     style="background-color: rgba(255, 255, 255, 0.7); z-index: 10; top: 0; left: 0;">
                  <div class="text-center">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2 text-primary fw-bold">Loading attendance data...</p>
                  </div>
                </div>

                <table class="table align-middle table-hover table-striped">
                  <thead class="table-light">
                  <tr>
                    <th @click="sortTable('empNo')" class="cursor-pointer">
                      Emp No. <i :class="getSortIcon('empNo')"></i>
                    </th>
                    <th @click="sortTable('employeeName')" class="cursor-pointer">
                      Employee Name <i :class="getSortIcon('employeeName')"></i>
                    </th>
                    <th>Department</th>
                    <th class="text-center">Check-in</th>
                    <th class="text-center">Check-out</th>
                    <th class="text-center">Remote Check-in</th>
                    <th class="text-center">Remote Check-out</th>
                    <th class="text-center">Hours Worked</th>
                    <th v-if="configuration.enableRollCalls" class="text-center">Roll Call 1</th>
                    <th v-if="configuration.enableRollCalls" class="text-center">Roll Call 2</th>
                    <th v-if="configuration.enableRollCalls" class="text-center">Roll Call 3</th>
                    <th class="text-center">Status</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="record in paginatedData" :key="record.empNo"
                      :class="getRowClass(record)">
                    <td>{{ record.empNo }}</td>
                    <td>{{ record.employeeName }}</td>
                    <td>{{ record.departmentName }}</td>

                    <!-- Check-in -->
                    <td class="text-center">
                      <div v-if="record.checkInTime && record.checkInTime !== '-'">
                          <span :class="getTimeStatusClass(record.checkInTime, 'checkin')">
                            {{ record.checkInTime }}
                          </span>
                        <small class="d-block text-muted" v-if="record.checkInLocation">
                          {{ truncateLocation(record.checkInLocation) }}
                        </small>
                      </div>
                      <span v-else class="text-danger">-</span>
                    </td>

                    <!-- Check-out -->
                    <td class="text-center">
                      <div v-if="record.checkOutTime && record.checkOutTime !== '-'">
                        <span>{{ record.checkOutTime }}</span>
                        <small class="d-block text-muted" v-if="record.checkOutLocation">
                          {{ truncateLocation(record.checkOutLocation) }}
                        </small>
                      </div>
                      <span v-else class="text-danger">-</span>
                    </td>

                    <!-- Remote Check-in -->
                    <td class="text-center">
                      <div v-if="record.remoteCheckInTime && record.remoteCheckInTime !== '-'">
                          <span :class="getTimeStatusClass(record.remoteCheckInTime, 'checkin')">
                            {{ record.remoteCheckInTime }}
                          </span>
                        <small class="d-block text-muted" v-if="record.remoteCheckInLocation">
                          {{ truncateLocation(record.remoteCheckInLocation) }}
                        </small>
                      </div>
                      <span v-else class="text-danger">-</span>
                    </td>

                    <!-- Remote Check-out -->
                    <td class="text-center">
                      <div v-if="record.remoteCheckOutTime && record.remoteCheckOutTime !== '-'">
                        <span>{{ record.remoteCheckOutTime }}</span>
                        <small class="d-block text-muted" v-if="record.remoteCheckOutLocation">
                          {{ truncateLocation(record.remoteCheckOutLocation) }}
                        </small>
                      </div>
                      <span v-else class="text-danger">-</span>
                    </td>

                    <!-- Hours Worked -->
                    <td class="text-center">
                        <span :class="getHoursClass(record.hoursWorked)">
                          {{ record.hoursWorked || '-' }}
                        </span>
                    </td>

                    <!-- Roll Calls -->
                    <td v-if="configuration.enableRollCalls" class="text-center">
                      <div v-if="record.rollCall1 && record.rollCall1 !== '-'" class="text-success">
                        <i class="mdi mdi-check-circle"></i> {{ record.rollCall1 }}
                        <small class="d-block text-muted" v-if="record.rollCall1Location">
                          {{ truncateLocation(record.rollCall1Location) }}
                        </small>
                      </div>
                      <span v-else class="text-danger"><i class="mdi mdi-close-circle"></i></span>
                    </td>

                    <td v-if="configuration.enableRollCalls" class="text-center">
                      <div v-if="record.rollCall2 && record.rollCall2 !== '-'" class="text-success">
                        <i class="mdi mdi-check-circle"></i> {{ record.rollCall2 }}
                        <small class="d-block text-muted" v-if="record.rollCall2Location">
                          {{ truncateLocation(record.rollCall2Location) }}
                        </small>
                      </div>
                      <span v-else class="text-danger"><i class="mdi mdi-close-circle"></i></span>
                    </td>

                    <td v-if="configuration.enableRollCalls" class="text-center">
                      <div v-if="record.rollCall3 && record.rollCall3 !== '-'" class="text-success">
                        <i class="mdi mdi-check-circle"></i> {{ record.rollCall3 }}
                        <small class="d-block text-muted" v-if="record.rollCall3Location">
                          {{ truncateLocation(record.rollCall3Location) }}
                        </small>
                      </div>
                      <span v-else class="text-danger"><i class="mdi mdi-close-circle"></i></span>
                    </td>

                    <!-- Status -->
                    <td class="text-center">
                        <span :class="getStatusBadgeClass(record)">
                          {{ getEmployeeStatus(record) }}
                        </span>
                    </td>
                  </tr>

                  <!-- No data row -->
                  <tr v-if="filteredData.length === 0 && !isLoading">
                    <td :colspan="getTableColumnCount()" class="text-center py-4">
                      <div class="text-muted">
                        <i class="mdi mdi-information-outline font-size-24 mb-2"></i>
                        <p>No attendance records found for the selected criteria.</p>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div class="d-flex justify-content-between align-items-center mt-4" v-if="filteredData.length > 0">
                <div>
                  Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalRecords }} entries
                </div>
                <div>
                  <ul class="pagination mb-0">
                    <li :class="['page-item', { disabled: currentPage === 1 }]">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                        <i class="mdi mdi-chevron-left"></i>
                      </a>
                    </li>
                    <li v-for="page in displayedPages" :key="page"
                        :class="['page-item', { active: currentPage === page }]">
                      <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li :class="['page-item', { disabled: currentPage === totalPages }]">
                      <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                        <i class="mdi mdi-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, execute } from "@/api";
import moment from "moment";
import _ from "lodash";
import * as XLSX from 'xlsx';

export default {
  name: "ReportDaily",
  components: {
    NavigationBar
  },
  data() {
    return {
      // User permissions
      isAdmin: false,

      // Configuration
      configuration: {
        enableRollCalls: true,
        minRequiredRollCalls: 3,
        minWorkingHours: 8,
        strictMode: true,
      },
      configurationChanged: false,
      configSaving: false,

      // Date and filters
      selectedDate: moment().format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
      selectedDepartment: '',
      searchQuery: '',
      attendanceFilter: 'all',

      // Data
      departments: [],
      reportData: [],
      filteredData: [],
      dashboardStats: {
        totalEmployees: 0,
        presentEmployees: 0,
        absentEmployees: 0,
        totalCheckins: 0,
        remoteCheckins: 0,
        onsiteCheckins: 0,
        rollCallResponses: 0,
        totalHours: '0',
        averageHours: '0'
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 20,

      // Sorting
      sortColumn: 'empNo',
      sortDirection: 'asc',

      // Loading states
      isLoading: false,
      isExporting: false,

      // Debounced search
      debouncedSearch: null
    };
  },

  computed: {
    totalRecords() {
      return this.filteredData.length;
    },

    totalPages() {
      return Math.ceil(this.totalRecords / this.itemsPerPage);
    },

    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredData.slice(start, end);
    },

    paginationStart() {
      return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
    },

    displayedPages() {
      const totalDisplayed = 5;
      const halfDisplay = Math.floor(totalDisplayed / 2);

      if (this.totalPages <= totalDisplayed) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      if (this.currentPage <= halfDisplay + 1) {
        return Array.from({ length: totalDisplayed }, (_, i) => i + 1);
      }

      if (this.currentPage >= this.totalPages - halfDisplay) {
        return Array.from({ length: totalDisplayed }, (_, i) => this.totalPages - totalDisplayed + i + 1);
      }

      return Array.from({ length: totalDisplayed }, (_, i) => this.currentPage - halfDisplay + i);
    },

    rollCallResponseRate() {
      if (!this.configuration.enableRollCalls || this.dashboardStats.totalEmployees === 0) {
        return 0;
      }
      const totalPossibleResponses = this.dashboardStats.totalEmployees * 3;
      return Math.round((this.dashboardStats.rollCallResponses / totalPossibleResponses) * 100);
    }
  },

  created() {
    this.debouncedSearch = _.debounce(this.applyFilters, 300);
    this.initializeComponent();
  },

  methods: {
    async initializeComponent() {
      await this.checkUserRole();
      await this.loadConfiguration();
      await this.loadDepartments();
      await this.loadAttendanceData();
    },

    // API calls
    async checkUserRole() {
      try {
        const data = new FormData();
        data.append("function", "getUserRole");

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.isAdmin = response.data.data?.role === 'admin';
        }
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    },

    async loadConfiguration() {
      try {
        const data = new FormData();
        data.append("function", "getSystemConfiguration");

        const response = await execute(data, authUrl);
        if (response.data.success && response.data.data?.configuration) {
          const config = response.data.data.configuration;
          this.configuration = {
            enableRollCalls: config.enableRollCalls ?? true,
            minRequiredRollCalls: parseInt(config.minRequiredRollCalls) || 3,
            minWorkingHours: parseFloat(config.minWorkingHours) || 8,
            strictMode: config.strictMode ?? true
          };
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
      }
    },

    async saveConfiguration() {
      if (!this.configurationChanged) return;

      this.configSaving = true;
      try {
        const data = new FormData();
        data.append("function", "saveSystemConfiguration");
        data.append("configuration", JSON.stringify(this.configuration));

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.configurationChanged = false;
          this.$toast.success('Configuration saved successfully');
          await this.loadAttendanceData(); // Refresh data with new configuration
        } else {
          this.$toast.error('Failed to save configuration');
        }
      } catch (error) {
        console.error("Error saving configuration:", error);
        this.$toast.error('Error saving configuration');
      } finally {
        this.configSaving = false;
      }
    },

    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getDepartments");

        const response = await execute(data, authUrl);
        if (response.data.success && response.data.data?.departments) {
          this.departments = response.data.data.departments;
        }
      } catch (error) {
        console.error("Error loading departments:", error);
      }
    },

    async loadAttendanceData() {
      this.isLoading = true;
      try {
        const data = new FormData();
        data.append("function", "getDailyAttendanceReportProcessed");
        data.append("date", this.selectedDate);
        data.append("configuration", JSON.stringify(this.configuration));

        if (this.selectedDepartment) {
          data.append("departmentId", this.selectedDepartment);
        }

        if (this.searchQuery.trim()) {
          data.append("searchQuery", this.searchQuery.trim());
        }

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.reportData = response.data.data.attendanceRecords || [];
          this.dashboardStats = response.data.data.dashboardStats || this.dashboardStats;
          this.applyFilters();
        } else {
          this.$toast.error(response.data.message || 'Failed to load attendance data');
          this.reportData = [];
          this.filteredData = [];
        }
      } catch (error) {
        console.error("Error loading attendance data:", error);
        this.$toast.error('Error loading attendance data');
        this.reportData = [];
        this.filteredData = [];
      } finally {
        this.isLoading = false;
      }
    },

    async exportToExcel() {
      if (this.filteredData.length === 0) {
        this.$toast.warning('No data to export');
        return;
      }

      this.isExporting = true;
      try {
        // Prepare export data
        const exportData = this.filteredData.map(record => {
          const baseRecord = {
            'Employee No': record.empNo,
            'Employee Name': record.employeeName,
            'Department': record.departmentName,
            'Check-in': record.checkInTime !== '-' ? record.checkInTime : '',
            'Check-out': record.checkOutTime !== '-' ? record.checkOutTime : '',
            'Remote Check-in': record.remoteCheckInTime !== '-' ? record.remoteCheckInTime : '',
            'Remote Check-out': record.remoteCheckOutTime !== '-' ? record.remoteCheckOutTime : '',
            'Hours Worked': record.hoursWorked || '',
            'Status': this.getEmployeeStatus(record)
          };

          if (this.configuration.enableRollCalls) {
            baseRecord['Roll Call 1'] = record.rollCall1 !== '-' ? record.rollCall1 : '';
            baseRecord['Roll Call 2'] = record.rollCall2 !== '-' ? record.rollCall2 : '';
            baseRecord['Roll Call 3'] = record.rollCall3 !== '-' ? record.rollCall3 : '';
          }

          return baseRecord;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Set column widths
        const columnWidths = [
          { wch: 15 }, // Employee No
          { wch: 25 }, // Employee Name
          { wch: 20 }, // Department
          { wch: 15 }, // Check-in
          { wch: 15 }, // Check-out
          { wch: 15 }, // Remote Check-in
          { wch: 15 }, // Remote Check-out
          { wch: 15 }, // Hours Worked
        ];

        if (this.configuration.enableRollCalls) {
          columnWidths.push({ wch: 15 }); // Roll Call 1
          columnWidths.push({ wch: 15 }); // Roll Call 2
          columnWidths.push({ wch: 15 }); // Roll Call 3
        }

        columnWidths.push({ wch: 15 }); // Status

        ws['!cols'] = columnWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Daily Attendance");

        // Generate filename
        let filename = `Daily_Attendance_${this.selectedDate}`;
        if (this.selectedDepartment) {
          const dept = this.departments.find(d => d.id == this.selectedDepartment);
          if (dept) {
            filename += `_${dept.departmentName.replace(/\s+/g, '_')}`;
          }
        }
        if (this.attendanceFilter !== 'all') {
          filename += `_${this.attendanceFilter}`;
        }
        filename += '.xlsx';

        // Write and download file
        XLSX.writeFile(wb, filename);
        this.$toast.success('Export completed successfully');
      } catch (error) {
        console.error('Export failed:', error);
        this.$toast.error('Export failed');
      } finally {
        this.isExporting = false;
      }
    },

    // Event handlers
    onConfigurationChange() {
      this.configurationChanged = true;
    },

    handleSearch() {
      this.debouncedSearch();
    },

    setAttendanceFilter(filter) {
      this.attendanceFilter = filter;
      this.currentPage = 1;
      this.applyFilters();
    },

    sortTable(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.applyFilters();
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    // Filtering and processing
    applyFilters() {
      let filtered = [...this.reportData];

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(record =>
            record.employeeName?.toLowerCase().includes(query) ||
            record.empNo?.toLowerCase().includes(query)
        );
      }

      // Apply attendance filter
      switch (this.attendanceFilter) {
        case 'present':
          filtered = filtered.filter(record => this.isEmployeePresent(record));
          break;
        case 'absent':
          filtered = filtered.filter(record => this.isEmployeeAbsent(record));
          break;
        case 'late':
          if (this.configuration.enableRollCalls) {
            filtered = filtered.filter(record => this.hasMissedRollCalls(record));
          }
          break;
      }

      // Apply sorting
      filtered.sort((a, b) => {
        const aValue = a[this.sortColumn] || '';
        const bValue = b[this.sortColumn] || '';

        const comparison = aValue.toString().localeCompare(bValue.toString());
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });

      this.filteredData = filtered;
      this.currentPage = 1;
    },

    // Helper methods
    isEmployeePresent(record) {
      const hasCheckin = (record.checkInTime && record.checkInTime !== '-') ||
          (record.remoteCheckInTime && record.remoteCheckInTime !== '-');

      if (!hasCheckin) return false;

      if (this.configuration.strictMode) {
        const hasCheckout = (record.checkOutTime && record.checkOutTime !== '-') ||
            (record.remoteCheckOutTime && record.remoteCheckOutTime !== '-');
        if (!hasCheckout) return false;
      }

      if (this.configuration.enableRollCalls) {
        const missedRollCalls = this.getMissedRollCallsCount(record);
        const maxAllowedMissed = 3 - this.configuration.minRequiredRollCalls;
        if (missedRollCalls > maxAllowedMissed) return false;
      }

      const hoursWorked = this.parseHours(record.hoursWorked);
      if (hoursWorked < this.configuration.minWorkingHours) return false;

      return true;
    },

    isEmployeeAbsent(record) {
      return !this.isEmployeePresent(record);
    },

    hasMissedRollCalls(record) {
      if (!this.configuration.enableRollCalls) return false;
      const missedCount = this.getMissedRollCallsCount(record);
      return missedCount > 0;
    },

    getMissedRollCallsCount(record) {
      let missed = 0;
      if (!record.rollCall1 || record.rollCall1 === '-') missed++;
      if (!record.rollCall2 || record.rollCall2 === '-') missed++;
      if (!record.rollCall3 || record.rollCall3 === '-') missed++;
      return missed;
    },

    parseHours(hoursStr) {
      if (!hoursStr || hoursStr === '-') return 0;
      const match = hoursStr.match(/(\d+\.?\d*)/);
      return match ? parseFloat(match[1]) : 0;
    },

    getEmployeeStatus(record) {
      if (this.isEmployeePresent(record)) {
        return 'Present';
      } else if (this.configuration.enableRollCalls && this.hasMissedRollCalls(record)) {
        return 'Missed Roll Calls';
      } else {
        return 'Absent';
      }
    },

    // UI helper methods
    getRowClass(record) {
      const status = this.getEmployeeStatus(record);
      switch (status) {
        case 'Present':
          return '';
        case 'Missed Roll Calls':
          return 'table-warning';
        case 'Absent':
          return 'table-danger';
        default:
          return '';
      }
    },

    getStatusBadgeClass(record) {
      const status = this.getEmployeeStatus(record);
      switch (status) {
        case 'Present':
          return 'badge bg-success';
        case 'Missed Roll Calls':
          return 'badge bg-warning';
        case 'Absent':
          return 'badge bg-danger';
        default:
          return 'badge bg-secondary';
      }
    },

    getTimeStatusClass(timeStr, type) {
      if (!timeStr || timeStr === '-') return 'text-danger';

      // You can add logic here to highlight late arrivals based on shift times
      // For now, just return default styling
      return '';
    },

    getHoursClass(hoursStr) {
      const hours = this.parseHours(hoursStr);
      if (hours === 0) return 'text-muted';
      if (hours < this.configuration.minWorkingHours) return 'text-danger fw-bold';
      if (hours < this.configuration.minWorkingHours + 1) return 'text-warning fw-bold';
      return 'text-success fw-bold';
    },

    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return 'bx bx-sort';
      }
      return this.sortDirection === 'asc' ? 'bx bx-sort-up' : 'bx bx-sort-down';
    },

    truncateLocation(location) {
      if (!location || location === '-') return '-';
      return location.length > 20 ? location.substring(0, 20) + '...' : location;
    },

    getTableColumnCount() {
      let count = 9; // Base columns
      if (this.configuration.enableRollCalls) {
        count += 3; // Roll call columns
      }
      return count;
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.mini-stats-wid {
  border: none;
  box-shadow: 0 0 35px 0 rgba(154, 161, 171, 0.15);
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.btn-group .btn {
  border-radius: 0;
}

.btn-group .btn:first-child {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.btn-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}
</style>